<template>
  <div class="pagamento-root" :class="{ 'pagamento-root--light': !$vuetify.theme.dark }">

    <!-- Top bar -->
    <div class="pay-topbar">
      <img :src="require('@/assets/images/zapermenu.png')" height="32" alt="ZaperMenu" />
      <div class="pay-topbar__secure">
        <v-icon small color="success">mdi-lock</v-icon>
        Pagamento 100% seguro
      </div>
    </div>

    <div class="pay-center">
      <div class="pay-card">

        <!-- Header -->
        <div class="pay-header">
          <div class="pay-header__icon">🎉</div>
          <h2 class="pay-header__title">Quase lá, {{ firstName }}!</h2>
          <p class="pay-header__desc">Revise seu pedido e finalize o pagamento para ativar sua conta.</p>
        </div>

        <!-- Resumo do plano -->
        <div class="pay-summary">
          <div class="pay-summary__title">Resumo do pedido</div>

          <div class="pay-summary__row">
            <span>{{ selectedPlan ? selectedPlan.label : 'Plano ZaperMenu' }}</span>
            <strong>R${{ selectedPlan ? selectedPlan.price : '—' }}/mês</strong>
          </div>

          <div class="pay-summary__row pay-summary__row--green">
            <span>🎁 Implementação por especialista</span>
            <strong>Grátis</strong>
          </div>

          <div class="pay-summary__row pay-summary__row--green">
            <span>🎁 Treinamento da equipe</span>
            <strong>Grátis</strong>
          </div>

          <div class="pay-summary__divider" />

          <div class="pay-summary__row pay-summary__row--total">
            <span>Total hoje</span>
            <strong>R${{ selectedPlan ? selectedPlan.price : '—' }}/mês</strong>
          </div>
        </div>

        <!-- ── Order Bump (Upgrade para Profissional) ── -->
        <div
          v-if="showBump"
          class="order-bump"
          :class="{ 'order-bump--active': bumpChecked }"
          role="checkbox"
          :aria-checked="bumpChecked"
          @click="toggleBump"
        >
          <!-- Checkbox visual -->
          <div class="bump-check" :class="{ 'bump-check--checked': bumpChecked }">
            <i v-if="bumpChecked" class="mdi mdi-check bump-check__icon"></i>
          </div>

          <!-- Conteúdo -->
          <div class="bump-body">
            <div class="bump-tag">⚡ Oferta exclusiva — só nessa tela</div>
            <div class="bump-title">Sim! Quero o Plano Profissional</div>
            <div class="bump-price">
              Adicione por apenas
              <strong>+R$62/mês</strong>
            </div>
            <div class="bump-features">
              <span v-for="f in bumpFeatures" :key="f.label" class="bump-feat">
                <i :class="'mdi mdi-' + f.icon"></i>{{ f.label }}
              </span>
            </div>
          </div>
        </div>

        <!-- Garantias rápidas -->
        <div class="pay-trust">
          <div class="pay-trust__item">
            <v-icon small color="success">mdi-shield-check</v-icon>
            <span>Garantia de 30 dias</span>
          </div>
          <div class="pay-trust__item">
            <v-icon small color="success">mdi-headset</v-icon>
            <span>Suporte humanizado</span>
          </div>
          <div class="pay-trust__item">
            <v-icon small color="success">mdi-cancel</v-icon>
            <span>Sem taxas por pedido</span>
          </div>
        </div>

        <!-- Botão de pagamento -->
        <button
          class="btn-pay"
          :class="{ 'btn-pay--loading': loading }"
          :disabled="loading"
          @click="pay"
        >
          <span v-if="!loading">
            <v-icon class="mr-2" small>mdi-credit-card-outline</v-icon>
            Pagar com Mercado Pago
          </span>
          <span v-else class="pay-loading">
            <div class="pay-spinner"></div>
            Redirecionando...
          </span>
        </button>

        <p v-if="error" class="pay-error">{{ error }}</p>

        <!-- Métodos de pagamento -->
        <div class="pay-methods">
          <v-icon small class="mr-1" color="grey">mdi-credit-card</v-icon>
          Cartão · PIX · Boleto
        </div>

        <div class="pay-secure">
          <v-icon small color="grey lighten-1">mdi-lock</v-icon>
          Ambiente seguro — seus dados são protegidos
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import { trackPlan } from '@/utils/track';
import { trackClick } from '@/utils/lpTracker';

export default {
  name: 'Pagamento',

  data: () => ({
    loading:      false,
    error:        null,
    bumpChecked:  false,
    showBump:     false,

    bumpFeatures: [
      { icon: 'robot-outline',          label: 'Atendente IA 24/7' },
      { icon: 'whatsapp',               label: 'Disparos API Oficial WhatsApp' },
      { icon: 'package-variant-closed', label: 'Controle de Estoque' },
      { icon: 'cash-register',          label: 'Controle de Caixa' },
      { icon: 'cellphone-nfc',          label: 'PIX Integrado' },
      { icon: 'headset',                label: 'Suporte Prioritário' },
    ],
  }),

  computed: {
    ...mapState(['selectedPlan', 'contatoUuid', 'quizAnswers']),

    firstName() {
      return this.quizAnswers?.nome?.split(' ')[0] || 'você';
    },
  },

  watch: {
    bumpChecked(checked) {
      if (checked) {
        const pro = { id: 'profissional', label: 'Plano Profissional', price: 259 };
        this.$store.commit('setSelectedPlan', pro);
        // Tracking — lead fez upgrade via order bump
        trackPlan('AddToCart', 'add_to_cart', pro);
      } else {
        this.$store.commit('setSelectedPlan', { id: 'essencial', label: 'Plano Essencial', price: 197 });
      }
    },
  },

  mounted() {
    if (!this.selectedPlan) {
      this.$router.replace('/');
      return;
    }
    // Mostra o bump apenas para quem escolheu o Essencial
    this.showBump = this.selectedPlan.id === 'essencial';

    // Tracking — lead chegou na página de pagamento (informou dados de pagamento)
    trackPlan('AddPaymentInfo', 'add_payment_info', this.selectedPlan);
  },

  methods: {
    toggleBump() {
      this.bumpChecked = !this.bumpChecked;
      trackClick(`Order Bump - ${this.bumpChecked ? 'ativado' : 'desativado'}`);
    },
    async pay() {
      trackClick(`Pagamento - Pagar com Mercado Pago (${this.selectedPlan?.label || 'plano'})`);
      this.loading = true;
      this.error = null;

      try {
        const res = await this.$http.post('pre-cadastro', {
          contato_uuid: this.contatoUuid,
          plano: this.selectedPlan?.id,
          valor: this.selectedPlan?.price,
        });

        const initPoint = res?.data?.init_point || res?.data?.data?.init_point;

        if (initPoint) {
          window.location.href = initPoint;
        } else {
          throw new Error('URL de pagamento não recebida.');
        }
      } catch (e) {
        console.error(e);
        this.error = 'Erro ao processar pagamento. Tente novamente ou entre em contato pelo WhatsApp.';
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.pagamento-root {
  min-height: 100vh;
  background: var(--page-bg, #0a0a0a);
  display: flex;
  flex-direction: column;
}

/* ── Top bar ── */
.pay-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 5%;
  background: #000;
  border-bottom: 1px solid rgba(255,255,255,0.08);

  &__secure {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: rgba(255,255,255,0.6);
  }
}

/* ── Center ── */
.pay-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

/* ── Card ── */
.pay-card {
  background: #111;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 24px;
  padding: 40px;
  width: 100%;
  max-width: 480px;
  color: #fff;

  @media (max-width: 600px) {
    padding: 28px 20px;
  }
}

/* ── Header ── */
.pay-header {
  text-align: center;
  margin-bottom: 28px;

  &__icon { font-size: 36px; margin-bottom: 10px; }

  &__title {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  &__desc {
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    line-height: 1.55;
  }
}

/* ── Plan summary ── */
.pay-summary {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 20px;

  &__title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.35);
    margin-bottom: 14px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 14px;
    color: rgba(255,255,255,0.7);
    border-bottom: 1px solid rgba(255,255,255,0.05);

    &:last-child { border-bottom: none; }
    strong { color: #fff; }

    &--green { color: #23cf65; strong { color: #23cf65; } }

    &--total {
      font-size: 16px;
      font-weight: 600;
      padding-top: 14px;
      strong { color: #23cf65; font-size: 20px; }
    }
  }

  &__divider {
    margin: 10px 0;
    border-top: 1px dashed rgba(255,255,255,0.1);
  }
}

/* ── Order Bump ── */
.order-bump {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  border: 2px dashed rgba(251, 191, 36, 0.45);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: border-color 0.25s, background 0.25s;
  background: rgba(251, 191, 36, 0.04);
  user-select: none;

  &:hover {
    border-color: rgba(251, 191, 36, 0.7);
    background: rgba(251, 191, 36, 0.07);
  }

  &--active {
    border: 2px solid #23cf65;
    background: rgba(35, 207, 101, 0.07);
    &:hover { background: rgba(35, 207, 101, 0.11); }
  }
}

.bump-check {
  flex-shrink: 0;
  margin-top: 2px;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid rgba(255, 255, 255, 0.25);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;

  &--checked {
    border-color: #23cf65;
    background: #23cf65;
  }
}

.bump-check__icon {
  font-size: 14px;
  color: #fff;
  font-weight: 700;
}

.bump-body {
  flex: 1;
  min-width: 0;
}

.bump-tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #fbbf24;
  margin-bottom: 5px;

  .order-bump--active & { color: #23cf65; }
}

.bump-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 3px;
  line-height: 1.3;
}

.bump-price {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 10px;

  strong {
    color: #fbbf24;
    font-weight: 700;
    .order-bump--active & { color: #23cf65; }
  }
}

.bump-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bump-feat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 99px;
  padding: 3px 9px;

  .mdi { font-size: 13px; opacity: 0.8; }
}

/* ── Trust bar ── */
.pay-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.pay-trust__item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 99px;
  background: rgba(35,207,101,0.06);
  border: 1px solid rgba(35,207,101,0.15);
  font-size: 12px;
  color: rgba(255,255,255,0.7);
}

/* ── Pay button ── */
.btn-pay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  background: #009ee3; /* Azul MercadoPago */
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  gap: 8px;

  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }

  &--loading { opacity: 0.7; }
}

.pay-loading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pay-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.pay-error {
  color: #ff6b6b;
  font-size: 13px;
  text-align: center;
  margin-top: 10px;
}

.pay-methods {
  text-align: center;
  font-size: 12px;
  color: rgba(255,255,255,0.35);
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pay-secure {
  text-align: center;
  font-size: 12px;
  color: rgba(255,255,255,0.25);
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

/* ══════════════════════════════════════
   LIGHT THEME OVERRIDES
══════════════════════════════════════ */
.pagamento-root--light {
  background: #f0f2f5;

  /* topbar mantém sempre dark para contraste com a logomarca */

  .pay-card {
    background: #fff;
    border-color: #e5e7eb;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    color: #111827;
  }

  .pay-header {
    &__title { color: #111827; }
    &__desc { color: #4b5563; }
  }

  .pay-summary {
    background: #f9fafb;
    border-color: #e5e7eb;

    &__title { color: #9ca3af; }

    &__row {
      color: #4b5563;
      border-bottom-color: #f3f4f6;
      strong { color: #111827; }
    }

    &__divider { border-top-color: #e5e7eb; }
  }

  .pay-trust__item {
    color: #374151;
    background: rgba(22,163,74,0.06);
    border-color: rgba(22,163,74,0.2);
  }

  .pay-methods { color: #9ca3af; }

  .pay-secure { color: #b0b7c3; }

  .order-bump {
    border-color: rgba(217, 119, 6, 0.4);
    background: rgba(251, 191, 36, 0.05);
    &:hover { background: rgba(251, 191, 36, 0.09); }
    &.order-bump--active {
      border-color: #16a34a;
      background: rgba(22, 163, 74, 0.06);
    }
  }

  .bump-check {
    border-color: #d1d5db;
    &--checked { border-color: #16a34a; background: #16a34a; }
  }

  .bump-title { color: #111827; }

  .bump-price {
    color: #6b7280;
    strong { color: #d97706; }
    .order-bump--active & strong { color: #16a34a; }
  }

  .bump-feat {
    color: #374151;
    background: #f3f4f6;
    border-color: #e5e7eb;
  }
}
</style>
